import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Banner from "../assets/images/banners/saudia-moments.png";
import imageMob from "../assets/images/banners/saudia-moments-mob.jpg";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../apis";
import { endpoints } from "../utils/endpoints";
import { useLanguage } from "../context/LanguageContext";
import { getLocalizedFieldValue } from "../utils/language";
import parse from "html-react-parser";
import he from "he";

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

const sliderData = [
  {
    id: 1,
    title: "Header",
    description:
      "Lorem ipsum dolor sit amet consectetur. Id pulvinar amet tempor elit felis sed odio eget. Et sit amet diam lectus donec integer nisl velit.",
    img: new URL("../assets/images/saudia-moments/1.png", import.meta.url).href,
  },
  {
    id: 2,
    title: "Header",
    description:
      "Lorem ipsum dolor sit amet consectetur. Id pulvinar amet tempor elit felis sed odio eget. Et sit amet diam lectus donec integer nisl velit.",
    img: new URL("../assets/images/saudia-moments/2.png", import.meta.url).href,
  },
  {
    id: 3,
    title: "Header",
    description:
      "Lorem ipsum dolor sit amet consectetur. Id pulvinar amet tempor elit felis sed odio eget. Et sit amet diam lectus donec integer nisl velit.",
    img: new URL("../assets/images/saudia-moments/3.jpg", import.meta.url).href,
  },
  {
    id: 4,
    title: "Header",
    description:
      "Lorem ipsum dolor sit amet consectetur. Id pulvinar amet tempor elit felis sed odio eget. Et sit amet diam lectus donec integer nisl velit.",
    img: new URL("../assets/images/saudia-moments/1.png", import.meta.url).href,
  }
];

function SaudiaMoments() {
  const { setBannerData } = useOutletContext();
  const [searchParams] = useSearchParams();
  const momentId = searchParams.get("momentId");
  const { language } = useLanguage();
  const [momentData, setMomentData] = useState(null);
  const [holidayPackages, setHolidayPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMomentDetails = useCallback(async () => {
    if (!momentId) return;
    
    setLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.saudiaMomentDetails}/${momentId}`);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const data = response?.data?.data;
        setMomentData(data?.saudiaMoment || null);
        setHolidayPackages(Array.isArray(data?.holidayPackageList) ? data.holidayPackageList : []);
      } else {
        setMomentData(null);
        setHolidayPackages([]);
      }
    } catch (error) {
      console.error("Error fetching moment details:", error);
      setMomentData(null);
      setHolidayPackages([]);
    } finally {
      setLoading(false);
    }
  }, [momentId]);

  useEffect(() => {
    if (momentId) {
      fetchMomentDetails();
    }
  }, [momentId, fetchMomentDetails]);

  // Get title from API or fallback
  const title = useMemo(() => {
    if (momentData) {
      return getLocalizedFieldValue(momentData, language, "title") || 
             (language === "AR" 
               ? "استمتع بلحظات مميزة في السعودية من خلال باقات عطلات السعودية الحصرية" 
               : "Experience Saudia Moments through exclusive Saudia Holiday packages");
    }
    return language === "AR" 
      ? "استمتع بلحظات مميزة في السعودية من خلال باقات عطلات السعودية الحصرية" 
      : "Experience Saudia Moments through exclusive Saudia Holiday packages";
  }, [momentData, language]);

  // Get description from API
  const description = useMemo(() => {
    if (momentData) {
      const desc = getLocalizedFieldValue(momentData, language, "discription") || 
                   getLocalizedFieldValue(momentData, language, "description") || "";
      return desc ? he.decode(desc) : "";
    }
    return "";
  }, [momentData, language]);

  // Get banner images from API or fallback
  const desktopBanner = useMemo(() => {
    if (momentData?.desktop_image) {
      return buildMediaUrl(momentData.desktop_image);
    }
    return Banner;
  }, [momentData]);

  const mobileBanner = useMemo(() => {
    if (momentData?.mobile_image) {
      return buildMediaUrl(momentData.mobile_image);
    }
    return imageMob;
  }, [momentData]);

  useEffect(() => {
    setBannerData({
      image: desktopBanner,
      MobileBanner: mobileBanner,
      title: "", // Commented out - title not shown in banner
      subtitle: "",
      skew: false,
      bannershow: true
    });
  }, [setBannerData, desktopBanner, mobileBanner]);
  return (
    <>
      <div className="container max-w-8xl mx-auto px-2 py-8 md:py-0 md:px-8 2xl:px-32 grid grid-cols-12">
        <div className="gap-x-16 col-span-12 text-white">
          {loading ? (
            <p className="text-white/90 mb-10 text-lg font-SaudiaLight">
              {language === "AR" ? "جاري التحميل..." : "Loading..."}
            </p>
          ) : (
            <>
              <h2 className="text-[24px] md:text-[2.625rem] font-SaudiaRegular mb-6 md:mb-10 leading-normal">
                {title}
              </h2>
              {description ? (
                <div className="text-white/90 mb-6 md:mb-10 md:text-lg font-SaudiaLight">
                  {parse(description)}
                </div>
              ) : (
                <>
                  <p className="text-white/90 mb-6 md:mb-10 md:text-lg font-SaudiaLight">
                    {language === "AR" 
                      ? "لوريم إيبسوم هو ببساطة نص شكلي (أي غير واضح) يُستخدم في صناعة الطباعة والتنضيد." 
                      : "Lorem ipsum dolor sit amet consectetur. Ullamcorper fermentum tempus cursus dolor praesent nisl phasellus. Dolor ullamcorper eget est amet sit sagittis auctor. Sit eget posuere nunc nunc mauris mauris dolor quis malesuada."}
                  </p>
                  <p className="text-white/90 mb-6 md:mb-10 md:text-lg font-SaudiaLight">
                    {language === "AR" 
                      ? "ظل لوريم إيبسوم النص الشكلي القياسي لهذه الصناعة منذ القرن السادس عشر." 
                      : "Lorem ipsum dolor sit amet consectetur. Ullamcorper fermentum tempus cursus dolor praesent nisl phasellus. Dolor ullamcorper eget est amet sit sagittis auctor."}
                  </p>
                </>
              )}
              <h2 className="text-2xl md:text-[2.625rem] font-SaudiaLight mb-6 md:mb-10 leading-normal">
                {language === "AR" ? "باقات عطلات السعودية" : "Saudia Holiday Packages"}
              </h2>
              <div className='skewSlider nagative'>
                <Splide
                  options={ {
                    rewind: true,
                    gap : '28px',
                    perPage : 2,
                    autoWidth: true,
                    focus: 0,
                    start: 0,
                    perMove: 1,
                    arrows: false
                  } }
                  aria-label="Holiday Packages"
                >
                  {holidayPackages.length > 0 ? (
                    // Show API holiday packages if available
                    holidayPackages.map((packageItem) => {
                      const packageTitle = getLocalizedFieldValue(packageItem, language, "title") || "";
                      const packageDescription = getLocalizedFieldValue(packageItem, language, "discription") || 
                                                 getLocalizedFieldValue(packageItem, language, "description") || "";
                      const packageImage = buildMediaUrl(packageItem?.desktop_image || packageItem?.mobile_image);
                      // Get purchase link based on language
                      const purchaseLink = language === "AR" 
                        ? packageItem?.purches_link_ar 
                        : packageItem?.purches_link_en;
                      
                      const handlePurchaseClick = () => {
                        if (purchaseLink) {
                          window.open(purchaseLink, "_blank", "noopener,noreferrer");
                        }
                      };
                      
                      return (
                        <SplideSlide key={packageItem._id} className="w-[320px] md:w-[600px]">
                          <div className="relative h-[180px] md:h-[330px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
                            {packageImage && (
                              <img className="absolute inset-0 w-full h-full object-cover" src={packageImage} alt={packageTitle}/>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
                          </div>
                          <div className="absolute left-3 md:left-5 caption px-3">
                            <h3 className="text-white text-lg md:text-[1.75rem] font-SaudiaBold">{packageTitle}</h3>
                            <p className="text-white/80 pr-4 md:pr-20 md:mt-1 text-xs md:text-lg font-SaudiaRegular leading-snug">{packageDescription}</p>
                            <button 
                              onClick={handlePurchaseClick}
                              disabled={!purchaseLink}
                              className="mt-2 md:mt-4 md:px-5 md:py-2 bg-saudiaGreen text-white rounded-sm md:rounded-md px-3 py-1.5 md:px-5 md:py-2 h-auto md:h-11 text-[10px] md:text-lg font-SaudiaLight md:font-SaudiaRegular whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {language === "AR" ? "شراء الباقة" : "Purchase package"}
                            </button>
                          </div>
                        </SplideSlide>
                      );
                    })
                  ) : (
                    // Show static holiday packages as fallback
                    sliderData.map((item) => (
                      <SplideSlide key={item.id} className="w-[320px] md:w-[600px]">
                        <div className="relative h-[180px] md:h-[330px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
                          {item.img && (
                            <img className="absolute inset-0 w-full h-full object-cover" src={item.img} alt={item.title}/>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
                        </div>
                        <div className="absolute left-3 md:left-5 caption px-3">
                          <h3 className="text-white text-lg md:text-[1.75rem] font-SaudiaBold">{item.title}</h3>
                          <p className="text-white/80 pr-4 md:pr-20 md:mt-1 text-xs md:text-lg font-SaudiaRegular leading-snug">{item.description}</p>
                          <button className="mt-2 md:mt-4 md:px-5 md:py-2 bg-saudiaGreen text-white rounded-sm md:rounded-md px-3 py-1.5 md:px-5 md:py-2 h-auto md:h-11 text-[10px] md:text-lg font-SaudiaLight md:font-SaudiaRegular whitespace-nowrap">
                            {language === "AR" ? "شراء الباقة" : "Purchase package"}
                          </button>
                        </div>
                      </SplideSlide>
                    ))
                  )}
                </Splide>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default SaudiaMoments
